// RoomEnvironment — cinematic per-floor backdrop.
//
// Each floor of the AI HQ gets a distinct architectural mood built from
// CSS gradients + inline SVG (no external assets, so it renders instantly
// and works offline). The compositions evoke the room's PURPOSE:
//
//   command        → sunrise city skyline through glass
//   sales          → strategic map of dots + trajectory arcs
//   pipeline       → deep-night boardroom with stage lighting
//   calls          → violet call floor with signal waveforms
//   communications → warm bullpen with drifting message glow
//   clients        → walnut boardroom, single portrait lamp
//   revenue        → gold vault with slow-drifting light beams
//   calendar       → night-city window with soft grid overlay
//   reports        → data floor with rising bar silhouettes
//   aiworkforce    → holographic ring of AI employees
//   compliance     → green datawall, verified pulses
//   settings       → cool tech ops floor, subtle circuit lines
//
// The environment layer is FIXED under the room content. It ignores
// pointer events so clicks always land on the real work UI. All
// motion is gated on `reduced` — reduced-motion users get the
// composition without any drift/pulse.

import React from 'react';
import hq from './theme';

export default function RoomEnvironment({ floor = 'command', accent, reduced = false }) {
  const paint = ENVIRONMENTS[floor] || ENVIRONMENTS.command;
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {paint({ accent: accent || hq.color.amber, reduced })}
      {/* Global vignette so content always reads. Every environment
          gets the same subtle darkening at the edges — it stitches
          the whole building into one visual language. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(120% 90% at 50% 0%, transparent 40%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// ── Environment palette factory ────────────────────────────────────
// Each entry returns a fragment of JSX. Kept as functions so accent
// color threads through cleanly and reduced-motion can gate anims.

const ENVIRONMENTS = {
  command:        commandEnv,
  sales:          salesEnv,
  pipeline:       pipelineEnv,
  calls:          callsEnv,
  communications: communicationsEnv,
  clients:        clientsEnv,
  revenue:        revenueEnv,
  calendar:       calendarEnv,
  reports:        reportsEnv,
  aiworkforce:    aiWorkforceEnv,
  compliance:     complianceEnv,
  settings:       settingsEnv,
  retention:      retentionEnv,
  growth:         growthEnv,
};

function commandEnv({ accent, reduced }) {
  return (
    <>
      {/* sunrise/city gradient — the executive lobby */}
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, #0b0e14 0%, #0d0f16 40%, #14100a 70%, ${withAlpha(accent, 0.22)} 100%)` }}/>
      {/* horizon glow */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: '18%', height: 220, background: `radial-gradient(60% 60% at 50% 100%, ${withAlpha(accent, 0.32)}, transparent 65%)`, filter: 'blur(20px)' }}/>
      {/* skyline silhouette */}
      <Skyline y="60%" opacity={0.75} />
      <Skyline y="66%" opacity={0.5} scale={0.72} shift={140} />
      {/* floor plane */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '32%', background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 100%)' }}/>
      {/* ceiling lights */}
      <CeilingLight accent={accent} reduced={reduced} />
    </>
  );
}

function salesEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0a0e14 0%, #0d1218 100%)' }}/>
      {/* strategic map */}
      <MapDots accent={accent} count={44} />
      {/* trajectory arcs */}
      <TrajectoryArcs accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function pipelineEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #08090d 0%, #10141b 100%)' }}/>
      {/* stage-lit floor */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '55%', background: `radial-gradient(80% 60% at 50% 100%, ${withAlpha(accent, 0.16)}, transparent 60%)` }}/>
      {/* pipeline lanes */}
      <PipelineLanes accent={accent} />
      <CeilingLight accent={accent} reduced={reduced} />
    </>
  );
}

function callsEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0a0912 0%, #120a1e 100%)' }}/>
      {/* violet ambient */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(60% 40% at 20% 80%, rgba(139,92,246,0.14), transparent 60%)' }}/>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(60% 40% at 80% 20%, rgba(239,68,68,0.10), transparent 60%)' }}/>
      <SignalWaves accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function communicationsEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0a0e14 0%, #0f151d 100%)' }}/>
      {/* bullpen warm glow */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(50% 40% at 20% 90%, ${withAlpha(accent, 0.14)}, transparent 60%)` }}/>
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(40% 30% at 80% 90%, ${withAlpha(accent, 0.10)}, transparent 60%)` }}/>
      <FloatingMessages accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function clientsEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0d0b09 0%, #14100b 100%)' }}/>
      {/* walnut boardroom */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(70% 50% at 50% 100%, ${withAlpha(accent, 0.16)}, transparent 60%)` }}/>
      {/* single portrait spotlight */}
      <div style={{ position: 'absolute', left: '15%', top: 60, width: 260, height: 320, borderRadius: '50%', background: `radial-gradient(circle, ${withAlpha(accent, 0.10)}, transparent 65%)`, filter: 'blur(30px)' }}/>
      <BoardTable accent={accent} />
      <CeilingLight accent={accent} reduced={reduced} />
    </>
  );
}

function revenueEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0b0a06 0%, #14100a 100%)' }}/>
      {/* vault gold */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(50% 40% at 50% 30%, ${withAlpha(accent, 0.20)}, transparent 60%)` }}/>
      <VaultBeams accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} />
    </>
  );
}

function calendarEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0a0d13 0%, #0e131b 100%)' }}/>
      {/* night city window */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(60% 40% at 60% 20%, ${withAlpha(accent, 0.14)}, transparent 60%)` }}/>
      <Skyline y="55%" opacity={0.4} />
      <CalendarGrid accent={accent} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function reportsEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0a0e0d 0%, #0f1417 100%)' }}/>
      {/* rising bar silhouettes */}
      <RisingBars accent={accent} />
      {/* trend line */}
      <TrendLine accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function aiWorkforceEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #07080c 0%, #0a0d13 100%)' }}/>
      {/* holographic ring floor */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '55%', background: `radial-gradient(60% 40% at 50% 100%, ${withAlpha(accent, 0.14)}, transparent 60%)` }}/>
      <HoloRing accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} />
    </>
  );
}

function complianceEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #080c0a 0%, #0c1310 100%)' }}/>
      {/* verified datawall */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(60% 40% at 50% 30%, ${withAlpha(accent, 0.14)}, transparent 60%)` }}/>
      <VerifiedGrid accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function settingsEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #090a0d 0%, #0d0f14 100%)' }}/>
      <CircuitLines accent={accent} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function retentionEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0d0a08 0%, #14100b 100%)' }}/>
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(70% 50% at 50% 100%, ${withAlpha(accent, 0.14)}, transparent 60%)` }}/>
      <Skyline y="60%" opacity={0.5} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

function growthEnv({ accent, reduced }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0a0d13 0%, #10141b 100%)' }}/>
      <MapDots accent={accent} count={30} />
      <TrendLine accent={accent} reduced={reduced} />
      <CeilingLight accent={accent} reduced={reduced} soft />
    </>
  );
}

// ── Building blocks (SVG) ──────────────────────────────────────────

function Skyline({ y = '55%', opacity = 0.7, scale = 1, shift = 0 }) {
  return (
    <svg
      viewBox="0 0 1600 220" preserveAspectRatio="none"
      style={{ position: 'absolute', left: 0, right: 0, top: y, width: '100%', height: 160, opacity, transform: `translateX(${shift}px) scaleY(${scale})`, transformOrigin: 'bottom' }}
    >
      <path d="M0,180 L60,180 L60,110 L110,110 L110,140 L160,140 L160,60 L220,60 L220,120 L260,120 L260,80 L320,80 L320,150 L360,150 L360,90 L420,90 L420,130 L470,130 L470,60 L520,60 L520,110 L570,110 L570,150 L620,150 L620,70 L680,70 L680,130 L740,130 L740,90 L800,90 L800,140 L860,140 L860,60 L920,60 L920,120 L980,120 L980,80 L1040,80 L1040,150 L1100,150 L1100,100 L1160,100 L1160,60 L1220,60 L1220,130 L1280,130 L1280,90 L1340,90 L1340,150 L1400,150 L1400,80 L1460,80 L1460,120 L1520,120 L1520,150 L1600,150 L1600,220 L0,220 Z"
        fill="#050709" />
      {/* window lights */}
      <g fill="#F5A62388">
        <rect x="72" y="130" width="4" height="6"/>
        <rect x="170" y="80" width="4" height="6"/>
        <rect x="230" y="90" width="4" height="6"/>
        <rect x="370" y="110" width="4" height="6"/>
        <rect x="480" y="90" width="4" height="6"/>
        <rect x="690" y="90" width="4" height="6"/>
        <rect x="810" y="110" width="4" height="6"/>
        <rect x="930" y="80" width="4" height="6"/>
        <rect x="1050" y="110" width="4" height="6"/>
        <rect x="1170" y="80" width="4" height="6"/>
        <rect x="1290" y="110" width="4" height="6"/>
        <rect x="1410" y="100" width="4" height="6"/>
      </g>
    </svg>
  );
}

function CeilingLight({ accent, reduced, soft }) {
  const opacity = soft ? 0.35 : 0.55;
  return (
    <div
      style={{
        position: 'absolute', top: -80, left: 0, right: 0, height: 220,
        background: `radial-gradient(50% 100% at 50% 0%, ${withAlpha(accent, opacity)}, transparent 70%)`,
        filter: 'blur(30px)',
        animation: reduced ? 'none' : 'ceiling-pulse 8s ease-in-out infinite',
      }}
    >
      <style>{`
        @keyframes ceiling-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .75; }
        }
      `}</style>
    </div>
  );
}

function MapDots({ accent, count = 40 }) {
  // Deterministic pseudo-random distribution so the map is stable
  // across renders (no reflow / no fake-flicker per repaint).
  const dots = React.useMemo(() => {
    const arr = [];
    let seed = 1;
    for (let i = 0; i < count; i++) {
      seed = (seed * 9301 + 49297) % 233280;
      const x = (seed / 233280) * 100;
      seed = (seed * 9301 + 49297) % 233280;
      const y = 20 + (seed / 233280) * 60;
      const r = 1 + ((i % 5) === 0 ? 3 : 0);
      arr.push({ x, y, r, key: i });
    }
    return arr;
  }, [count]);
  return (
    <svg
      viewBox="0 0 100 100" preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }}
    >
      {dots.map(d => (
        <circle key={d.key} cx={d.x} cy={d.y} r={d.r * 0.35} fill={accent} opacity={d.r > 1 ? 0.9 : 0.5}/>
      ))}
    </svg>
  );
}

function TrajectoryArcs({ accent, reduced }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="arc-grad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor={accent} stopOpacity="0"/>
          <stop offset="0.5" stopColor={accent} stopOpacity="0.5"/>
          <stop offset="1" stopColor={accent} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d="M 200 720 Q 800 200 1400 640" fill="none" stroke="url(#arc-grad)" strokeWidth="1"/>
      <path d="M 320 780 Q 800 300 1300 720" fill="none" stroke="url(#arc-grad)" strokeWidth="1" opacity="0.6"/>
      {!reduced && (
        <circle r="4" fill={accent}>
          <animateMotion dur="7s" repeatCount="indefinite" path="M 200 720 Q 800 200 1400 640" />
        </circle>
      )}
    </svg>
  );
}

function PipelineLanes({ accent }) {
  const lanes = [0.28, 0.42, 0.58, 0.74];
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.4 }}>
      {lanes.map((y, i) => (
        <rect key={i} x="60" y={y * 900} width="1480" height="1" fill={accent} opacity={0.3 + i * 0.1}/>
      ))}
    </svg>
  );
}

function SignalWaves({ accent, reduced }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.4 }}>
      {[0.4, 0.55, 0.7].map((y, i) => (
        <path key={i} d={`M 0 ${y * 900} Q 400 ${y * 900 - 40} 800 ${y * 900} T 1600 ${y * 900}`} stroke={accent} strokeWidth="1" fill="none" opacity={0.5 - i * 0.15}>
          {!reduced && <animate attributeName="opacity" values="0.2;0.7;0.2" dur={`${4 + i}s`} repeatCount="indefinite"/>}
        </path>
      ))}
    </svg>
  );
}

function FloatingMessages({ accent, reduced }) {
  const msgs = [
    { x: 12, y: 32, w: 10 },
    { x: 74, y: 38, w: 8 },
    { x: 28, y: 62, w: 12 },
    { x: 66, y: 66, w: 9 },
  ];
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.35 }}>
      {msgs.map((m, i) => (
        <rect key={i} x={m.x} y={m.y} width={m.w} height="2.4" rx="1.2" fill={accent} opacity={0.5}>
          {!reduced && <animate attributeName="opacity" values="0.3;0.7;0.3" dur={`${3 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.4}s`}/>}
        </rect>
      ))}
    </svg>
  );
}

function BoardTable({ accent }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, width: '100%', height: '60%', opacity: 0.5 }}>
      <ellipse cx="800" cy="820" rx="720" ry="120" fill={accent} opacity="0.06"/>
      <ellipse cx="800" cy="800" rx="620" ry="70" fill="none" stroke={accent} strokeOpacity="0.28" strokeWidth="1"/>
    </svg>
  );
}

function VaultBeams({ accent, reduced }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.35 }}>
      <defs>
        <linearGradient id="beam-grad" x1="0.5" x2="0.5" y1="0" y2="1">
          <stop offset="0" stopColor={accent} stopOpacity="0.35"/>
          <stop offset="1" stopColor={accent} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {[300, 620, 940, 1260].map((x, i) => (
        <rect key={i} x={x} y="0" width="60" height="900" fill="url(#beam-grad)">
          {!reduced && <animate attributeName="opacity" values="0.4;0.9;0.4" dur={`${6 + i}s`} repeatCount="indefinite" begin={`${i * 0.5}s`}/>}
        </rect>
      ))}
    </svg>
  );
}

function CalendarGrid({ accent }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.14 }}>
      {[0.4, 0.5, 0.6, 0.7, 0.8].map((y, i) => (
        <line key={`h${i}`} x1="0" x2="1600" y1={y * 900} y2={y * 900} stroke={accent} strokeWidth="1"/>
      ))}
      {Array.from({ length: 7 }).map((_, i) => (
        <line key={`v${i}`} x1={i * 228} x2={i * 228} y1="0" y2="900" stroke={accent} strokeWidth="1"/>
      ))}
    </svg>
  );
}

function RisingBars({ accent }) {
  const bars = [12, 18, 14, 24, 20, 34, 26, 42, 30, 48, 36, 60];
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, width: '100%', height: '55%', opacity: 0.4 }}>
      {bars.map((h, i) => (
        <rect key={i} x={i * 128 + 30} y={900 - h * 8} width="70" height={h * 8} fill={accent} opacity={0.35 + (i / bars.length) * 0.4}/>
      ))}
    </svg>
  );
}

function TrendLine({ accent, reduced }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }}>
      <path d="M 0 700 L 200 680 L 400 620 L 600 640 L 800 520 L 1000 500 L 1200 420 L 1400 380 L 1600 280" stroke={accent} strokeWidth="2" fill="none"/>
      {!reduced && (
        <circle r="6" fill={accent}>
          <animateMotion dur="9s" repeatCount="indefinite" path="M 0 700 L 200 680 L 400 620 L 600 640 L 800 520 L 1000 500 L 1200 420 L 1400 380 L 1600 280"/>
        </circle>
      )}
    </svg>
  );
}

function HoloRing({ accent, reduced }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.7 }}>
      <ellipse cx="800" cy="700" rx="620" ry="120" fill="none" stroke={accent} strokeWidth="1.5" opacity="0.6"/>
      <ellipse cx="800" cy="700" rx="480" ry="90" fill="none" stroke={accent} strokeWidth="1" opacity="0.35"/>
      <ellipse cx="800" cy="700" rx="340" ry="64" fill="none" stroke={accent} strokeWidth="1" opacity="0.2"/>
      {!reduced && (
        <circle r="6" fill={accent}>
          <animateMotion dur="10s" repeatCount="indefinite">
            <mpath xlinkHref="#holo-orbit"/>
          </animateMotion>
        </circle>
      )}
      <defs>
        <path id="holo-orbit" d="M 800 580 A 620 120 0 1 1 799 580 Z"/>
      </defs>
    </svg>
  );
}

function VerifiedGrid({ accent, reduced }) {
  const rows = 4, cols = 8;
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push({ r, c, key: `${r}-${c}` });
    }
  }
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.28 }}>
      {cells.map((c, i) => (
        <g key={c.key} transform={`translate(${140 + c.c * 170},${240 + c.r * 130})`}>
          <rect width="120" height="90" rx="6" fill="none" stroke={accent} strokeWidth="1" opacity="0.55"/>
          <path d="M 30 45 L 50 60 L 90 30" stroke={accent} strokeWidth="1.4" fill="none" opacity="0.8">
            {!reduced && <animate attributeName="opacity" values="0.4;1;0.4" dur={`${4 + (i % 4)}s`} repeatCount="indefinite" begin={`${(i % 8) * 0.3}s`}/>}
          </path>
        </g>
      ))}
    </svg>
  );
}

function CircuitLines({ accent }) {
  return (
    <svg viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.22 }}>
      <path d="M 0 200 L 400 200 L 400 350 L 900 350 L 900 500 L 1600 500" stroke={accent} strokeWidth="1" fill="none"/>
      <path d="M 0 620 L 300 620 L 300 720 L 1200 720 L 1200 600 L 1600 600" stroke={accent} strokeWidth="1" fill="none"/>
      <circle cx="400" cy="200" r="4" fill={accent}/>
      <circle cx="900" cy="350" r="4" fill={accent}/>
      <circle cx="300" cy="620" r="4" fill={accent}/>
      <circle cx="1200" cy="720" r="4" fill={accent}/>
    </svg>
  );
}

// ── util ───────────────────────────────────────────────────────────

function withAlpha(hex, alpha) {
  // Accept #rrggbb / #rgb / already-rgba passthrough.
  if (!hex) return `rgba(245,166,35,${alpha})`;
  if (hex.startsWith('rgba') || hex.startsWith('rgb')) return hex;
  const c = hex.replace('#', '');
  const norm = c.length === 3 ? c.split('').map(x => x + x).join('') : c;
  const r = parseInt(norm.slice(0, 2), 16);
  const g = parseInt(norm.slice(2, 4), 16);
  const b = parseInt(norm.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
